/**
 * Iter 68 — Super-admin team management.
 *
 * Renders a table of all admin accounts with inline edit (name / email /
 * super-admin toggle / active flag), a "Force-reset password" dialog, and
 * a "Deactivate" button. Visible only to super-admins; non-super admins
 * see a polite gate explaining they don't have access.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, Plus, KeyRound, UserMinus, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function AdminTeamPanel() {
  const [team, setTeam] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [pwdResetTarget, setPwdResetTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/team").then((res) => res.data);
      setTeam(r.items || []);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
        setTeam([]);
        return;
      }
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  if (forbidden) {
    return (
      <Card data-testid="admin-team-forbidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Team members</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-neutral-600">
          You need <strong>super-admin</strong> permissions to manage other admin accounts. Ask an
          existing super-admin to grant you the role.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="admin-team-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Admin team</CardTitle>
          <CardDescription>Add, edit, and remove admin accounts.</CardDescription>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="admin-team-add-button">
          <Plus className="h-4 w-4 mr-1" /> Add admin
        </Button>
      </CardHeader>
      <CardContent>
        {team === null ? (
          <div className="py-8 text-center text-sm text-neutral-500"><Loader2 className="h-4 w-4 mx-auto animate-spin" /></div>
        ) : team.length === 0 ? (
          <div className="py-8 text-center text-sm text-neutral-500">No admin accounts yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b">
                  <th className="py-2">Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {team.map((u) => (
                  <TeamRow key={u.id} user={u} onChanged={load} onResetPwd={() => setPwdResetTarget(u)} onDeactivate={() => setDeactivateTarget(u)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <CreateAdminDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => { setCreateOpen(false); load(); }}
      />
      <ResetPasswordDialog
        target={pwdResetTarget}
        onOpenChange={(o) => !o && setPwdResetTarget(null)}
        onDone={() => setPwdResetTarget(null)}
      />
      <DeactivateDialog
        target={deactivateTarget}
        onOpenChange={(o) => !o && setDeactivateTarget(null)}
        onDone={() => { setDeactivateTarget(null); load(); }}
      />
    </Card>
  );
}

function TeamRow({ user, onChanged, onResetPwd, onDeactivate }) {
  const [savingSuper, setSavingSuper] = useState(false);

  const toggleSuper = async (next) => {
    setSavingSuper(true);
    try {
      await api.put(`/admin/team/${user.id}`, { is_super_admin: next });
      toast.success(`${user.email} ${next ? "promoted to" : "demoted from"} super-admin.`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingSuper(false);
    }
  };

  return (
    <tr className="border-b last:border-b-0" data-testid={`admin-team-row-${user.id}`}>
      <td className="py-3 font-medium">{user.name || "—"}</td>
      <td className="text-neutral-700">{user.email}</td>
      <td>
        <div className="flex items-center gap-2">
          <Switch
            checked={!!user.is_super_admin}
            onCheckedChange={toggleSuper}
            disabled={savingSuper}
            data-testid={`admin-team-super-switch-${user.id}`}
          />
          <span className="text-xs text-neutral-600">{user.is_super_admin ? "Super-admin" : "Admin"}</span>
        </div>
      </td>
      <td>
        {user.is_active === false ? (
          <Badge variant="secondary" className="bg-neutral-100 text-neutral-600">Inactive</Badge>
        ) : (
          <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
        )}
      </td>
      <td className="text-right">
        <div className="inline-flex gap-1">
          <Button size="sm" variant="outline" onClick={onResetPwd} data-testid={`admin-team-reset-pwd-${user.id}`}>
            <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset password
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onDeactivate}
            disabled={user.is_active === false}
            data-testid={`admin-team-deactivate-${user.id}`}
            className="text-rose-700 hover:text-rose-800"
          >
            <UserMinus className="h-3.5 w-3.5 mr-1" /> Deactivate
          </Button>
        </div>
      </td>
    </tr>
  );
}

function CreateAdminDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSuper, setIsSuper] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => { setName(""); setEmail(""); setPassword(""); setIsSuper(false); };
  const submit = async () => {
    if (name.length < 2) return toast.error("Name is too short.");
    if (!email.includes("@")) return toast.error("Enter a valid email.");
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    setSaving(true);
    try {
      await api.post("/admin/team", { name, email: email.toLowerCase(), password, is_super_admin: isSuper });
      toast.success(`${email} added.`);
      reset();
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-team-create-dialog">
        <DialogHeader>
          <DialogTitle>Add admin</DialogTitle>
          <DialogDescription>Creates a new admin account they can log in with immediately.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="create-name">Name</Label>
            <Input id="create-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-team-create-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="create-email">Email</Label>
            <Input id="create-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-team-create-email" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="create-pwd">Temporary password</Label>
            <Input id="create-pwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-team-create-password" />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Switch checked={isSuper} onCheckedChange={setIsSuper} data-testid="admin-team-create-super" />
            <Label className="text-sm">Grant super-admin (can manage other admins)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="admin-team-create-confirm">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Create admin
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ target, onOpenChange, onDone }) {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPwd(""); }, [target]);

  const submit = async () => {
    if (pwd.length < 8) return toast.error("Password must be at least 8 characters.");
    setSaving(true);
    try {
      await api.post(`/admin/team/${target.id}/reset-password`, { new_password: pwd });
      toast.success(`Password reset for ${target.email}. Share the new password securely.`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>Force-set a new password for <strong>{target?.email}</strong>.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="reset-pwd">New password</Label>
          <Input id="reset-pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Set new password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeactivateDialog({ target, onOpenChange, onDone }) {
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.delete(`/admin/team/${target.id}`);
      toast.success(`${target.email} deactivated.`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Deactivate admin</DialogTitle>
          <DialogDescription>
            <strong>{target?.email}</strong> will no longer be able to sign in. Their record stays
            in the database so audit logs keep their author attribution.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Deactivate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
